import React, { useMemo } from 'react';
import { format, parseISO, differenceInSeconds } from 'date-fns';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
// Import types and hooks
import { ClipModelFromServer } from '../hooks/useAllClipsQuery';
import { AUTO_DIRECTOR_CAMERA_LABEL } from '../types';
import useEnqueueClipForExportMutation from '../hooks/useEnqueueClipForExportMutation';
import { getTeamLogoUrl } from '@/shared/services/getTeamLogoUrl';
import useClipDownloadMutation from '../hooks/useClipDownloadMutation';
import useDownloadLogsMutation from '../hooks/useDownloadLogsMutation';
import useDownloadConfigMutation from '../hooks/useDownloadConfigMutation';
import useCancelClipExportMutation from '../hooks/useCancelClipExportMutation';
import StatusIndicator from '@/shared/components/ClipStatus';
import DateTimeCell from '@/shared/components/DateTimeCell';
import { ROW_ACTIONS_SX, TOOLBAR_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import { getAbbreviation } from '@/shared/utils/getAbbreviation';
import { cx } from '@/styles/muiTheme';
import useTrackedEntityQuery from '../hooks/useTrackedEntityQuery';
import useGameCameraQuery from '../hooks/useGameCameraQuery';

// Component to display camera information
const CameraCell: React.FC<{ gameId?: string; cameraId?: number; autoDirector?: boolean }> = ({
  gameId,
  cameraId,
  autoDirector,
}) => {
  const { data: camera, isLoading, error } = useGameCameraQuery(gameId, cameraId);

  // Auto-director clips have no camera row: the player cut between all of them.
  if (autoDirector) {
    return (
      <Typography variant="caption" sx={{ fontWeight: 500 }}>
        {AUTO_DIRECTOR_CAMERA_LABEL}
      </Typography>
    );
  }

  if (isLoading) {
    return <Typography variant="caption">Loading...</Typography>;
  }

  if (error || !camera) {
    return <Typography variant="caption">-</Typography>;
  }

  const name = camera.displayName || `Camera ${camera.camIndex}`;

  return (
    <Stack spacing={0.5}>
      {/* Abbreviated to keep the column narrow; the full name stays reachable
          on hover, since an initialism on its own can be ambiguous. */}
      <Tooltip title={name} arrow>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {getAbbreviation(name)}
        </Typography>
      </Tooltip>
    </Stack>
  );
};

// Component to display tracked entity information
const TrackedEntityCell: React.FC<{ gameId?: string; entityId?: number }> = ({
  gameId,
  entityId,
}) => {
  const { data: entity, isLoading, error } = useTrackedEntityQuery(gameId, entityId);

  if (isLoading) {
    return <Typography variant="caption">Loading...</Typography>;
  }

  if (error || !entity) {
    return <Typography variant="caption">-</Typography>;
  }

  if (entity.type === 'Player') {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        {entity.headshot && (
          <Box
            component="img"
            src={entity.headshot}
            alt={entity.displayName}
            sx={{ width: 24, height: 24, borderRadius: '50%' }}
          />
        )}
        <Stack>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            #{entity.playerNumber} {entity.displayName}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
            {entity.playerPosition} - {entity.playerCategory}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  // BallOrPuck entity
  return (
    <Typography variant="caption" sx={{ fontStyle: 'italic', textTransform: 'capitalize' }}>
      {entity.displayName}
    </Typography>
  );
};

// Auto-refresh choices, in ms. 0 = off.
const REFRESH_INTERVALS = [
  { value: 0, label: 'Off' },
  { value: 5_000, label: '5s' },
  { value: 10_000, label: '10s' },
  { value: 30_000, label: '30s' },
  { value: 60_000, label: '1m' },
];

const ClipsTable: React.FC<{
  setPaginationModel: React.Dispatch<React.SetStateAction<{ page: number; pageSize: number }>>;
  paginationModel: { page: number; pageSize: number };
  isLoading: boolean;
  error: unknown;
  data: { rowCount: number; rows: ClipModelFromServer[] } | undefined;
  refreshInterval: number;
  setRefreshInterval: (ms: number) => void;
}> = ({ setPaginationModel, paginationModel, isLoading, error, data, refreshInterval, setRefreshInterval }) => {
  // Export mutation hook
  const exportClipMutation = useEnqueueClipForExportMutation();
  const downloadClipMutation = useClipDownloadMutation();
  const downloadExportLogsMutation = useDownloadLogsMutation();
  const downloadConfigMutation = useDownloadConfigMutation();
  const cancelExportMutation = useCancelClipExportMutation();

  const [canceling, setCanceling] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  // Handle cancel export clip
  const handleCancelExportClip = async (clipId: string) => {
    setCanceling(true);
    await cancelExportMutation.mutate({ clipId: parseInt(clipId) });
    setCanceling(false);
  };

  // Handle export clip
  const handleExportClip = async (clipId: string) => {
    setExporting(true);
    await exportClipMutation.mutate(parseInt(clipId));
    setExporting(false);
  };

  const handleDownloadClip = (clipId: string) => {
    downloadClipMutation.mutate({ clipId, video: true, source: 'clips_table' });
  };

  const handleDownloadExportLogs = (clipId: string) => {
    downloadExportLogsMutation.mutate(parseInt(clipId));
  };

  const handleDownloadConfig = (clipId: string) => {
    downloadConfigMutation.mutate(parseInt(clipId));
  };

  // Memoized columns
  const columns = useMemo<MRT_ColumnDef<ClipModelFromServer>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 10,
        muiTableBodyCellProps: { sx: { color: cx.text.muted } },
      },
      {
        accessorKey: 'createdBy',
        header: 'Created By',
        size: 150,
        Cell: ({ row }) => (
          <Typography variant="caption" sx={{ textDecoration: 'lowercase' }}>
            {row.original.clouduser.email}
          </Typography>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        size: 150,
        Cell: ({ row }) => <DateTimeCell value={row.original.createdAt} />,
      },
      {
        accessorKey: 'game',
        header: 'Game',
        size: 200,
        Cell: ({ row }) => {
          const game = row.original.game;
          return (
            <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
              <img
                src={getTeamLogoUrl(game.awayTeam)}
                alt={`${game.awayTeam.name} logo`}
                width={30}
                height={30}
              />
              <Typography>{game.awayTeam.abbreviation}</Typography>
              <Typography variant="body2">@</Typography>
              <Typography>{game.homeTeam.abbreviation}</Typography>
              <img
                src={getTeamLogoUrl(game.homeTeam)}
                alt={`${game.homeTeam.name} logo`}
                width={30}
                height={30}
              />
            </Stack>
          );
        },
      },
      {
        accessorKey: 'objectTrackingId',
        header: 'Tracked Object',
        size: 200,
        Cell: ({ row }) => (
          <>
            {row.original.cameraMode === 'fixed' ? (
              <>Fixed Position</>
            ) : (
              <TrackedEntityCell
                gameId={row.original.game?.uniqueId}
                entityId={row.original.objectTrackingId}
              />
            )}
          </>
        ),
      },
      {
        accessorKey: 'cameraId',
        header: 'Camera',
        // Was 180 to fit a full name like "Left Blue Line"; the cell now renders
        // a 2-3 letter initialism, so the header is what sets the floor.
        size: 90,
        Cell: ({ row }) => (
          <CameraCell
            gameId={row.original.game?.uniqueId}
            cameraId={row.original.cameraId}
            autoDirector={row.original.autoDirector}
          />
        ),
      },
      {
        accessorKey: 'startTimestamp',
        header: 'Start Time',
        enableSorting: true,
        // Holds "07:30:00 PM" at the theme's 12px body size, plus the header's
        // sort arrow and column-actions button.
        size: 100,
        Cell: ({ row }) => (
          <Typography variant="body2">
            {row.original?.startTimestamp
              ? format(parseISO(row.original.startTimestamp), 'hh:mm:ss a')
              : ''}
          </Typography>
        ),
      },
      {
        accessorKey: 'duration',
        header: 'Duration',
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => {
          if (!row.original?.startTimestamp || !row.original?.endTimestamp) {
            return <Typography variant="body2">-</Typography>;
          }
          const start = parseISO(row.original.startTimestamp);
          const end = parseISO(row.original.endTimestamp);
          const durationSeconds = differenceInSeconds(end, start);
          const minutes = Math.floor(durationSeconds / 60);
          const seconds = durationSeconds % 60;
          return (
            <Typography variant="body2">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </Typography>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 100,
        Cell: ({ row }) => {
          if (row.original.mediatype === 'bookmark') return null;
          return <StatusIndicator status={row.original.status} />;
        },
      },
    ],
    [],
  );

  return (
    <MaterialReactTable
      columns={columns}
      data={data?.rows || []}
      enablePagination
      manualPagination
      rowCount={data?.rowCount || 0}
      muiTopToolbarProps={{ sx: TOOLBAR_ACTIONS_SX }}
      renderTopToolbarCustomActions={() => (
        <TextField
          select
          label="Auto-refresh"
          value={refreshInterval}
          onChange={(e) => setRefreshInterval(Number(e.target.value))}
          size="small"
          sx={{ minWidth: 140, m: 1 }}
        >
          {REFRESH_INTERVALS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      state={{
        isLoading,
        pagination: {
          pageIndex: paginationModel.page,
          pageSize: paginationModel.pageSize,
        },
      }}
      onPaginationChange={(updater) => {
        const newPagination =
          typeof updater === 'function'
            ? updater({ pageIndex: paginationModel.page, pageSize: paginationModel.pageSize })
            : updater;
        setPaginationModel({
          page: newPagination.pageIndex,
          pageSize: newPagination.pageSize,
        });
      }}
      muiTableBodyRowProps={({ row }) => ({
        sx: {
          opacity: row.original.deleted ? 0.3 : 1,
          '&:hover': {
            opacity: row.original.deleted ? 0.8 : 1,
          },
          cursor: 'pointer',
        },
      })}
      renderRowActions={({ row }) => (
        // The per-button `&.Mui-disabled` overrides that used to sit on each
        // IconButton set a near-black color, which is invisible on a dark row.
        // ROW_ACTIONS_SX handles the disabled state for the whole group.
        <Box sx={ROW_ACTIONS_SX}>
          <Tooltip title="Download Clip">
            <IconButton
              onClick={() => handleDownloadClip(row.original.id)}
              disabled={row.original.mediatype === 'bookmark' || row.original.status !== 'READY'}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Config">
            <IconButton
              onClick={() => handleDownloadConfig(row.original.id)}
              disabled={row.original.mediatype === 'bookmark' || row.original.status !== 'ERROR'}
            >
              <CodeOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Logs">
            <IconButton
              onClick={() => handleDownloadExportLogs(row.original.id)}
              disabled={row.original.mediatype === 'bookmark' || row.original.status !== 'ERROR'}
            >
              <ReceiptLongOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Retry Clip">
            <IconButton
              onClick={() => handleExportClip(row.original.id)}
              disabled={
                row.original.mediatype === 'bookmark' ||
                !['ERROR', 'CANCELED', 'UNPROCESSED', 'NEW'].includes(row.original.status)
              }
              loading={exporting}
            >
              <ReplayOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel Clip">
            {/* Red, like the other destructive actions: cancelling aborts an
                export that is already running. ROW_ACTIONS_SX picks up
                color="error" and still greys the button out when disabled. */}
            <IconButton
              color="error"
              loading={canceling}
              onClick={() => handleCancelExportClip(row.original.id)}
              disabled={
                row.original.mediatype === 'bookmark' || !row.original.status.endsWith('ING')
              }
            >
              <CancelOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      enableRowActions={true}
      positionActionsColumn="last"
    />
  );
};

export default ClipsTable;
